import { BaseAPICallsHandler } from '../BaseAPICallsHandler';
import { NetworkUtils } from '../NetworkUtils';
import { ServerConfig } from '../ServerConfig';

export enum UploadPurpose {
  BUYER_PROFILE_PHOTO = 'BUYER_PROFILE_PHOTO',
  SELLER_PROFILE_PHOTO = 'SELLER_PROFILE_PHOTO',
  SELLER_COVER_IMAGE = 'SELLER_COVER_IMAGE',
  SELLER_UNDERTAKING_DOCUMENT = 'SELLER_UNDERTAKING_DOCUMENT',
  GIG_PORTFOLIO_IMAGE = 'GIG_PORTFOLIO_IMAGE',
  GIG_COVER_IMAGE = 'GIG_COVER_IMAGE',
  ORDER_DELIVERY_ORIGINAL = 'ORDER_DELIVERY_ORIGINAL',
  ORDER_DELIVERY_PREVIEW = 'ORDER_DELIVERY_PREVIEW',
  REVISION_REQUEST_ATTACHMENT = 'REVISION_REQUEST_ATTACHMENT',
  PROGRESS_REVIEW_ATTACHMENT = 'PROGRESS_REVIEW_ATTACHMENT',
  ADMIN_CATEGORY_IMAGE = 'ADMIN_CATEGORY_IMAGE',
  ADMIN_SUBCATEGORY_IMAGE = 'ADMIN_SUBCATEGORY_IMAGE',
}

export interface SignedUploadRequest {
  url: string;
  method: 'PUT';
  headers: Record<string, string>;
  expiresAt: string;
}

export interface UploadSession {
  sessionId: string;
  strategy: 'SINGLE' | 'MULTIPART';
  partSizeBytes: number;
  totalParts: number;
  completedParts: number[];
  status: 'INITIATED' | 'IN_PROGRESS' | 'PAUSED' | 'COMPLETED' | 'ABORTED';
  expiresAt: string;
  singleUpload?: SignedUploadRequest;
}

export interface UploadAsset {
  assetId: string;
  visibility: 'PUBLIC' | 'PRIVATE';
  url?: string;
  sizeBytes: number;
  mimeType: string;
}

export class UploadAPIHandler {
  public static shared = new UploadAPIHandler();
  private readonly apiHandler = BaseAPICallsHandler.shared;
  private readonly serverConfig = ServerConfig.shared;
  private readonly networkUtils = NetworkUtils.shared;

  private authHeaders() {
    return this.networkUtils.addAuthHeaders({});
  }

  private adminAuthHeaders() {
    return this.networkUtils.addAdminAuthHeaders({});
  }

  async createSession(input: { purpose: UploadPurpose; fileName: string; mimeType: string; sizeBytes: number; resourceId?: string }): Promise<UploadSession> {
    const response = await this.apiHandler.makePostRequest(this.authHeaders(), this.serverConfig.uploadAPIs.createSessionAPI, input);
    return response.data as UploadSession;
  }

  async createAdminSession(input: { purpose: UploadPurpose; fileName: string; mimeType: string; sizeBytes: number }): Promise<UploadSession> {
    const response = await this.apiHandler.makePostRequest(this.adminAuthHeaders(), this.serverConfig.uploadAPIs.createAdminSessionAPI, input);
    return response.data as UploadSession;
  }

  async getSession(sessionId: string): Promise<UploadSession> {
    const response = await this.apiHandler.makeGetRequest(this.authHeaders(), this.serverConfig.uploadAPIs.sessionByIdAPI(sessionId));
    return response.data as UploadSession;
  }

  async signParts(sessionId: string, partNumbers: number[]): Promise<Array<SignedUploadRequest & { partNumber: number }>> {
    const response = await this.apiHandler.makePostRequest(this.authHeaders(), this.serverConfig.uploadAPIs.partsAPI(sessionId), { partNumbers });
    return response.data as Array<SignedUploadRequest & { partNumber: number }>;
  }

  async reportParts(sessionId: string, parts: Array<{ partNumber: number; entityTag: string; sizeBytes: number }>): Promise<UploadSession> {
    const response = await this.apiHandler.makePatchRequest(this.authHeaders(), this.serverConfig.uploadAPIs.partsAPI(sessionId), { parts });
    return response.data as UploadSession;
  }

  async pause(sessionId: string): Promise<UploadSession> {
    const response = await this.apiHandler.makePostRequest(this.authHeaders(), this.serverConfig.uploadAPIs.pauseSessionAPI(sessionId), {});
    return response.data as UploadSession;
  }

  async resume(sessionId: string): Promise<UploadSession> {
    const response = await this.apiHandler.makePostRequest(this.authHeaders(), this.serverConfig.uploadAPIs.resumeSessionAPI(sessionId), {});
    return response.data as UploadSession;
  }

  async complete(sessionId: string): Promise<UploadAsset> {
    const response = await this.apiHandler.makePostRequest(this.authHeaders(), this.serverConfig.uploadAPIs.completeSessionAPI(sessionId), {});
    return response.data as UploadAsset;
  }

  async completeAdmin(sessionId: string): Promise<UploadAsset> {
    const response = await this.apiHandler.makePostRequest(this.adminAuthHeaders(), this.serverConfig.uploadAPIs.completeAdminSessionAPI(sessionId), {});
    return response.data as UploadAsset;
  }

  async abort(sessionId: string): Promise<void> {
    await this.apiHandler.makeDeleteRequest(this.authHeaders(), this.serverConfig.uploadAPIs.sessionByIdAPI(sessionId), {});
  }

  async getReadUrl(assetId: string): Promise<string> {
    const response = await this.apiHandler.makeGetRequest(this.authHeaders(), this.serverConfig.uploadAPIs.assetReadUrlAPI(assetId));
    return response.data.url as string;
  }
}